import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type ControllerDecisionFilter, type ControllerDecisionView, type ControllerKillScope, type ControllerKillView, type ControllerLever, type ControllerMode, type IncentiveControllerView } from '@vello/api-client';
import {
  maySelfApprove,
  controllerModeTone,
  controllerPolicyTone,
  decisionOutcomeTone,
  effectiveControllerMode,
  formatMarketInstant,
  groupIncentiveZones,
  incentiveControllerAuthority,
  killCanProposeReenable,
  killStatusTone,
  marketTimeZone,
  observedLine,
  reenableDecisionBlock,
  tailOf,
} from '../../src/index';
import { useAdmin } from '../session';
import { usePersonNames } from '../components/person-name';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, Badge, Button, EmptyState, Field, FilterBar, FormRow, Notice, Panel, Row, Select, Stack, Table, Text, useAutoRefresh } from '@vello/web-theme';

/**
 * The controller's panels of the cockpit (W10-CONTROLLER-RULES, D495, ADR-0112; W10-INCENTIVE-COCKPIT, D497, ADR-0117; doc 17 section 3.4), over
 * `IncentiveControllerAdminApi` (`/v1/admin/incentive-controller/*`).
 *
 * For the market in the header scope (never typed):
 * - **mode and policy**: the market's mode (Off / Shadow / Rules) and the state of the governed `incentives.controllerPolicy` - no policy, no controller - with the rule table and
 *   the shadow period; the mode itself changes through the governed policy (four eyes), never here;
 * - **the kill switch**: ONE operator switches the controller off for the market (or, with country-level access, the whole country), for one lever or all, and the controller's live
 *   offers end at once; switching it back on is FOUR EYES - one operator proposes, a DIFFERENT operator approves or refuses (the proposer's buttons are hidden, with the reason);
 * - **the decision log**: every evaluation, newest first - the zone, the lever, the mode, the state the zone was in, the action, WHY (a reason code read through the vocabulary), what it
 *   saw and what came of it.
 * Every code shown is read through the central vocabulary. A refused or failed read is an ERROR - never an empty list. No native dialog is ever opened. The data refreshes itself.
 */

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string } | undefined;
const note = (text: string): ReactElement => <EmptyState title={text} />;
const LOG_LIMIT = 50;
interface DecisionFilterState {
  readonly zone: string;
  readonly lever: '' | ControllerLever | 'NONE';
  readonly mode: '' | ControllerMode;
  readonly outcome: '' | 'LOGGED' | 'APPLIED' | 'REFUSED';
}
/** One shared object: setting it when the filter is already empty changes nothing (no second read). */
const EMPTY_FILTER: DecisionFilterState = { zone: '', lever: '', mode: '', outcome: '' };
const OUTCOMES = ['LOGGED', 'APPLIED', 'REFUSED'] as const;
const decisionQuery = (f: { zone: string; lever: string; mode: string; outcome: string }): ControllerDecisionFilter => ({
  limit: LOG_LIMIT,
  ...(f.zone !== '' ? { zone: f.zone } : {}),
  ...(f.lever !== '' ? { lever: f.lever as ControllerLever | 'NONE' } : {}),
  ...(f.mode !== '' ? { mode: f.mode as ControllerMode } : {}),
  ...(f.outcome !== '' ? { outcome: f.outcome as (typeof OUTCOMES)[number] } : {}),
});

export function IncentiveControllerPanels({ market }: { readonly market: string }): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  const { mayView, mayManage, mayApprove } = incentiveControllerAuthority(grants);
  const [loaded, setLoaded] = useState('');
  const [view, setView] = useState<IncentiveControllerView | undefined>(undefined);
  const [log, setLog] = useState<readonly ControllerDecisionView[]>([]);
  const [zoneNames, setZoneNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);
  const [killScope, setKillScope] = useState<ControllerKillScope>('MARKET');
  const [killLever, setKillLever] = useState<'' | ControllerLever>('');
  const [killReason, setKillReason] = useState('OPERATOR_KILL_SWITCH');
  const [reenableReason, setReenableReason] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<DecisionFilterState>(EMPTY_FILTER);
  const seq = useRef(0);
  const personName = usePersonNames((view?.kills ?? []).flatMap((k) => [k.killedBy?.id, k.reenableProposedBy?.id]));
  const actorText = (a: { readonly id: string; readonly label?: string | null } | null | undefined): string => (a == null ? '—' : (a.label ?? personName(a.id) ?? tailOf(a.id)));

  const refusal = useCallback(
    (e: { code: string; reasonCode?: string; detail?: string }): string => (e.code === 'FORBIDDEN' && e.reasonCode === undefined ? t('ctl.refusal.forbidden') : t('ctl.error', { code: fail(e) })),
    [t, fail],
  );
  const failText = useCallback((e: { code: string; reasonCode?: string; detail?: string }): string => (e.reasonCode !== undefined || e.detail !== undefined ? fail(e) : refusal(e)), [fail, refusal]);

  const load = useCallback(
    async (m: string, f: DecisionFilterState = filter): Promise<void> => {
      const mine = ++seq.current;
      const target = m.trim();
      if (target === '' || !mayView) {
        setLoaded(target);
        setView(undefined);
        setLog([]);
        return;
      }
      const [state, decisions] = await Promise.all([runtime.incentiveController.state(target), runtime.incentiveController.decisions(target, decisionQuery(f))]);
      if (mine !== seq.current) return;
      setLoaded(target);
      if (state.ok && decisions.ok) {
        setView(state.value);
        setLog(decisions.value.decisions);
        setError(undefined);
      } else if (!state.ok) {
        // A refused read keeps the data unknown: the screen says it could not load, never "no decisions".
        setError(refusal(state.error));
      } else if (!decisions.ok) {
        setError(refusal(decisions.error));
      }
      const zones = await runtime.ops.incentiveZones(target);
      if (mine !== seq.current || !zones.ok) return;
      setZoneNames(new Map(groupIncentiveZones(zones.value.zones).filter((z) => z.approved !== undefined).map((z) => [z.zoneId, (z.approved ?? z.latest).definition.name])));
    },
    [runtime, mayView, refusal, filter],
  );

  useEffect(() => {
    setView(undefined);
    setLog([]);
    setError(undefined);
    // W10K-07: another market starts with an EMPTY filter - its log is never read through the filter set on the previous one.
    setFilter(EMPTY_FILTER);
    void load(market, EMPTY_FILTER);
    // Reload when the header scope names another market.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  // A filter of the log re-reads it WITHOUT clearing the screen: the filters stay where they are under the operator's hand.
  const firstFilter = useRef(true);
  useEffect(() => {
    if (firstFilter.current) {
      firstFilter.current = false;
      return;
    }
    void load(market);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  useAutoRefresh(() => load(market), { intervalMs: AUTO_REFRESH_MS.queue, enabled: mayView && market !== '' && !busy });

  const tz = marketTimeZone(loaded, markets);
  const when = (iso: string | null | undefined): string => (iso === null || iso === undefined ? '—' : formatMarketInstant(iso, tz, lang));
  const zoneLabel = (zoneId: string): string => (zoneId === 'MARKET' ? t('ctl.zone.market') : zoneNames.has(zoneId) ? t('ctl.zone.drawn', { name: zoneNames.get(zoneId) ?? '' }) : t('ctl.zone.unknown'));
  const leverLabel = (lever: string): string => (lever === 'NONE' ? t('ctl.log.noLever') : v('active_incentive.lever', lever));
  const rulesText = (levers: readonly string[]): string => (levers.length === 0 ? t('ctl.state.rules.none') : levers.map((l) => v('active_incentive.lever', l)).join(', '));
  const scopeLabel = (k: ControllerKillView): string => (k.scope === 'COUNTRY' ? `${v('controller.kill_scope', 'COUNTRY')} ${k.country}` : v('controller.kill_scope', 'MARKET'));

  const kill = async (): Promise<void> => {
    setBusy(true);
    const res = await runtime.incentiveController.kill({ marketId: loaded, scope: killScope, ...(killLever !== '' ? { lever: killLever } : {}), reasonCode: killReason });
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: failText(res.error) });
    setMsg({ tone: 'success', text: t('ctl.kill.done', { ended: res.value.ended }) });
    await load(loaded);
  };
  const propose = async (k: ControllerKillView): Promise<void> => {
    setBusy(true);
    const res = await runtime.incentiveController.proposeReenable(k.killId, { marketId: loaded, reasonCode: reenableReason[k.killId] ?? view?.reenableReasons[0] ?? 'INCIDENT_RESOLVED' });
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: failText(res.error) });
    setMsg({ tone: 'success', text: t('ctl.reenable.proposedDone') });
    await load(loaded);
  };
  const decide = async (k: ControllerKillView, decision: 'APPROVE' | 'REFUSE'): Promise<void> => {
    setBusy(true);
    const res = await runtime.incentiveController.decideReenable(k.killId, { marketId: loaded, decision });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: failText(res.error) });
      await load(loaded);
      return;
    }
    setMsg({ tone: 'success', text: t(decision === 'APPROVE' ? 'ctl.reenable.approvedDone' : 'ctl.reenable.refusedDone') });
    await load(loaded);
  };

  const killAction = (k: ControllerKillView): ReactElement => {
    if (k.status === 'LIFTED') return <span>—</span>;
    if (k.status === 'KILLED') {
      if (!killCanProposeReenable(k, mayManage)) return <Text role="caption" tone="tertiary">{t('ctl.reenable.noPermission')}</Text>;
      return (
        <Stack gap={2}>
          <Select aria-label={t('ctl.reenable.reason')} value={reenableReason[k.killId] ?? view?.reenableReasons[0] ?? ''} onChange={(ev) => setReenableReason((r) => ({ ...r, [k.killId]: ev.target.value }))}>
            {(view?.reenableReasons ?? []).map((r) => (
              <option key={r} value={r}>{v('controller.kill_reason', r)}</option>
            ))}
          </Select>
          <Button type="button" disabled={busy} onClick={() => void propose(k)}>{t('ctl.reenable.propose')}</Button>
        </Stack>
      );
    }
    const block = reenableDecisionBlock(k, me?.id, mayApprove, selfApproval);
    if (block === undefined) {
      return (
        <Stack gap={2}>
          <Text role="caption" tone="tertiary">{t('ctl.reenable.proposedBy', { who: actorText(k.reenableProposedBy) })}</Text>
          <Row gap={2} align="center" wrap>
            <Button type="button" variant="success" disabled={busy} onClick={() => void decide(k, 'APPROVE')}>{t('ctl.reenable.approve')}</Button>
            <Button type="button" variant="danger" disabled={busy} onClick={() => void decide(k, 'REFUSE')}>{t('ctl.reenable.refuse')}</Button>
          </Row>
        </Stack>
      );
    }
    return <Text role="caption" tone="tertiary">{block === 'OWN_PROPOSAL' ? t('ctl.reenable.own') : block === 'NO_PERMISSION' ? t('ctl.reenable.noPermission') : block === 'UNKNOWN_OPERATOR' ? t('ctl.reenable.unknownOperator') : '—'}</Text>;
  };

  const policy = view?.policy;
  const mode = view !== undefined ? effectiveControllerMode(view) : 'OFF';

  return (
    <>
      {!mayView ? (
        <Panel>{note(t('ctl.noPermission'))}</Panel>
      ) : (
        <>
          {(error !== undefined || msg !== undefined) && (
            <Panel>
              {error !== undefined && <Notice tone="danger">{error}</Notice>}
              {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
            </Panel>
          )}

          {view !== undefined && policy !== undefined && (
            <>
              <Panel>
                <Stack gap={3}>
                  <h3>{t('ctl.state.title')}</h3>
                  <Row gap={2} align="center" wrap>
                    <Text role="caption" tone="tertiary">{t('ctl.state.mode')}</Text>
                    <Badge tone={controllerModeTone(mode)}>{v('controller.mode', mode)}</Badge>
                    <Text role="caption" tone="tertiary">{t('ctl.state.policy')}</Text>
                    <Badge tone={controllerPolicyTone(policy.state)}>{v('incentive_fund.policy_state', policy.state)}</Badge>
                    <Text role="caption" tone="tertiary">{t('ctl.state.version', { version: policy.policyVersion })}</Text>
                  </Row>
                  {policy.state === 'UNSET' || policy.state === 'INVALID' ? <Notice tone="warning">{t('ctl.state.unset')}</Notice> : null}
                  {policy.mode === 'RULES' && !view.shadow.met ? <Notice tone="warning">{t('ctl.state.shadowPending', { days: view.shadow.minShadowDays })}</Notice> : null}
                  {policy.rules !== undefined && (
                    <Text role="caption" tone="tertiary">{t('ctl.state.rules', { supply: rulesText(policy.rules.supplyShort), demand: rulesText(policy.rules.demandShort) })}</Text>
                  )}
                  {policy.intervalSeconds !== undefined && policy.decisionTtlSeconds !== undefined && policy.lookbackWindows !== undefined && (
                    <Text role="caption" tone="tertiary"><span className="tnum">{t('ctl.state.cadence', { interval: policy.intervalSeconds, ttl: Math.round(policy.decisionTtlSeconds / 60), windows: policy.lookbackWindows })}</span></Text>
                  )}
                  <Text role="caption" tone="tertiary">{t('ctl.state.where')}</Text>
                </Stack>
              </Panel>

              {mayManage && (
                <Panel>
                  <Stack gap={3}>
                    <h3>{t('ctl.kill.title')}</h3>
                    <Text role="caption" tone="tertiary">{t('ctl.kill.hint')}</Text>
                    <FormRow action={<Button type="button" variant="danger" disabled={busy} onClick={() => void kill()}>{t('ctl.kill.submit')}</Button>}>
                      <Field label={t('ctl.kill.scope')} htmlFor="ctl-kill-scope">
                        <Select id="ctl-kill-scope" value={killScope} onChange={(ev) => setKillScope(ev.target.value as ControllerKillScope)}>
                          <option value="MARKET">{v('controller.kill_scope', 'MARKET')}</option>
                          <option value="COUNTRY">{v('controller.kill_scope', 'COUNTRY')}</option>
                        </Select>
                      </Field>
                      <Field label={t('ctl.kill.lever')} htmlFor="ctl-kill-lever">
                        <Select id="ctl-kill-lever" value={killLever} onChange={(ev) => setKillLever(ev.target.value as '' | ControllerLever)}>
                          <option value="">{t('ctl.kill.lever.all')}</option>
                          {view.levers.map((l) => (
                            <option key={l} value={l}>{v('active_incentive.lever', l)}</option>
                          ))}
                        </Select>
                      </Field>
                      <Field label={t('ctl.kill.reason')} htmlFor="ctl-kill-reason">
                        <Select id="ctl-kill-reason" value={killReason} onChange={(ev) => setKillReason(ev.target.value)}>
                          {view.killReasons.map((r) => (
                            <option key={r} value={r}>{v('controller.kill_reason', r)}</option>
                          ))}
                        </Select>
                      </Field>
                    </FormRow>
                  </Stack>
                </Panel>
              )}

              <Panel padded={false}>
                <Stack gap={3}>
                  <h3>{t('ctl.kill.list.title')}</h3>
                  <Table
                    columns={[
                      { key: 'scope', header: t('ctl.col.scope'), render: (k: ControllerKillView) => <span>{scopeLabel(k)}</span> },
                      { key: 'lever', header: t('ctl.col.lever'), render: (k: ControllerKillView) => <span>{k.lever === null ? t('ctl.kill.lever.all') : v('active_incentive.lever', k.lever)}</span> },
                      { key: 'status', header: t('ctl.col.status'), render: (k: ControllerKillView) => <Badge tone={killStatusTone(k.status)}>{v('controller.kill_status', k.status)}</Badge> },
                      { key: 'reason', header: t('ctl.col.reason'), render: (k: ControllerKillView) => <span>{v('controller.kill_reason', k.reasonCode)}</span> },
                      { key: 'by', header: t('ctl.col.by'), render: (k: ControllerKillView) => <span className="tnum">{t('ctl.kill.by', { who: actorText(k.killedBy), when: when(k.killedAt) })}</span> },
                      { key: 'action', header: t('ctl.col.action'), render: killAction },
                    ]}
                    rows={view.kills}
                    rowKey={(k) => k.killId}
                    empty={note(t('ctl.kill.empty'))}
                  />
                </Stack>
              </Panel>

              <Panel padded={false}>
                <Stack gap={3}>
                  <h3>{t('ctl.log.title')}</h3>
                  <FilterBar label={t('ctl.filter.label')}>
                    <Field label={t('ctl.log.col.zone')} htmlFor="ctl-f-zone">
                      <Select id="ctl-f-zone" value={filter.zone} onChange={(ev) => setFilter((f) => ({ ...f, zone: ev.target.value }))}>
                        <option value="">{t('ctl.filter.all')}</option>
                        <option value="MARKET">{t('ctl.zone.market')}</option>
                        {[...zoneNames.entries()].map(([id, name]) => (
                          <option key={id} value={id}>{t('ctl.zone.drawn', { name })}</option>
                        ))}
                      </Select>
                    </Field>
                    <Field label={t('ctl.log.col.lever')} htmlFor="ctl-f-lever">
                      <Select id="ctl-f-lever" value={filter.lever} onChange={(ev) => setFilter((f) => ({ ...f, lever: ev.target.value as typeof f.lever }))}>
                        <option value="">{t('ctl.filter.all')}</option>
                        {view.levers.map((l) => (
                          <option key={l} value={l}>{v('active_incentive.lever', l)}</option>
                        ))}
                        <option value="NONE">{t('ctl.log.noLever')}</option>
                      </Select>
                    </Field>
                    <Field label={t('ctl.log.col.mode')} htmlFor="ctl-f-mode">
                      <Select id="ctl-f-mode" value={filter.mode} onChange={(ev) => setFilter((f) => ({ ...f, mode: ev.target.value as typeof f.mode }))}>
                        <option value="">{t('ctl.filter.all')}</option>
                        {view.modes.map((m) => (
                          <option key={m} value={m}>{v('controller.mode', m)}</option>
                        ))}
                      </Select>
                    </Field>
                    <Field label={t('ctl.log.col.outcome')} htmlFor="ctl-f-outcome">
                      <Select id="ctl-f-outcome" value={filter.outcome} onChange={(ev) => setFilter((f) => ({ ...f, outcome: ev.target.value as typeof f.outcome }))}>
                        <option value="">{t('ctl.filter.all')}</option>
                        {OUTCOMES.map((o) => (
                          <option key={o} value={o}>{v('controller.outcome', o)}</option>
                        ))}
                      </Select>
                    </Field>
                  </FilterBar>
                  <Table
                    columns={[
                      { key: 'when', header: t('ctl.log.col.when'), render: (d: ControllerDecisionView) => <span className="tnum">{when(d.decidedAt)}</span> },
                      { key: 'zone', header: t('ctl.log.col.zone'), render: (d: ControllerDecisionView) => <span>{zoneLabel(d.zoneId)}</span> },
                      { key: 'lever', header: t('ctl.log.col.lever'), render: (d: ControllerDecisionView) => <span>{leverLabel(d.lever)}</span> },
                      { key: 'mode', header: t('ctl.log.col.mode'), render: (d: ControllerDecisionView) => <Badge tone={controllerModeTone(d.mode)}>{v('controller.mode', d.mode)}</Badge> },
                      {
                        key: 'state', header: t('ctl.log.col.state'),
                        render: (d: ControllerDecisionView) => {
                          const saw = observedLine(d);
                          return (
                            <Stack gap={1}>
                              <span>{v('controller.zone_state', d.state)}</span>
                              {saw !== undefined && (
                                <Text role="caption" tone="tertiary">
                                  <span className="tnum">{t('ctl.log.saw', { requests: saw.requests, rate: saw.noDriverRateBp === null ? '—' : Math.round(saw.noDriverRateBp / 100), free: saw.driversFree, online: saw.driversOnline })}</span>
                                </Text>
                              )}
                            </Stack>
                          );
                        },
                      },
                      { key: 'action', header: t('ctl.log.col.action'), render: (d: ControllerDecisionView) => <span>{v('controller.action', d.action)}</span> },
                      { key: 'why', header: t('ctl.log.col.why'), render: (d: ControllerDecisionView) => <span>{v('controller.reason', d.reasonCode)}</span> },
                      {
                        key: 'outcome', header: t('ctl.log.col.outcome'),
                        render: (d: ControllerDecisionView) => (
                          <Stack gap={1}>
                            <Badge tone={decisionOutcomeTone(d.outcome)}>{v('controller.outcome', d.outcome)}</Badge>
                            {d.outcomeReason !== null && <Text role="caption" tone="tertiary">{d.outcomeReason.startsWith('INCENTIVE_') ? v('problem.active_incentive', d.outcomeReason) : v('controller.reason', d.outcomeReason)}</Text>}
                          </Stack>
                        ),
                      },
                    ]}
                    rows={log}
                    rowKey={(d) => d.decisionId}
                    empty={note(t('ctl.log.empty'))}
                  />
                </Stack>
              </Panel>
            </>
          )}
        </>
      )}
    </>
  );
}
